window.themeLog = window.themeLog || function () {};
/**
 * WISHLIST MANAGEMENT SYSTEM
 *
 * COOKIE CLASSIFICATIE: ESSENTIEEL / FUNCTIONEEL
 * Deze cookies zijn noodzakelijk voor de kernfunctionaliteit van de website
 * en vallen onder "strictly necessary cookies" volgens AVG/GDPR.
 * Geen toestemming vereist - net als winkelwagen cookies.
 *
 * Cookie details:
 * - Naam: intimatered_wishlist
 * - Doel: Opslaan van verlanglijst (product handles)
 * - Bewaartermijn: 30 dagen
 * - Type: Functioneel (essentieel)
 * - Scope: First-party
 * - Gegevens: Alleen product handles (geen persoonlijke informatie)
 */

(function() {
  'use strict';

  const WISHLIST_COOKIE = 'intimatered_wishlist';
  const COOKIE_DAYS = 30;

  // Cookie utilities
  function setCookie(name, value, days) {
    const date = new Date();
    date.setTime(date.setTime() + (days * 24 * 60 * 60 * 1000));
    const expires = `expires=${date.toUTCString()}`;
    document.cookie = `${name}=${value};${expires};path=/;SameSite=Lax`;
  }

  function getCookie(name) {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for(let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  }

  // Wishlist class
  class Wishlist {
    constructor() {
      this.items = [];
      this.drawer = null;
      this.overlay = null;
      this.isOpen = false;
      this.load();
      this.initDrawer();
      this.updateUI();
      this.attachEventListeners();
    }

    initDrawer() {
      this.drawer = document.querySelector('[data-wishlist-drawer]');
      this.overlay = document.querySelector('[data-wishlist-drawer-overlay]');
    }

    load() {
      // Probeer eerst cookie, dan localStorage als fallback
      const cookieData = getCookie(WISHLIST_COOKIE);
      const localData = localStorage.getItem(WISHLIST_COOKIE);

      if (cookieData) {
        try {
          this.items = JSON.parse(decodeURIComponent(cookieData));
        } catch (e) {
          console.error('Error parsing wishlist cookie:', e);
          this.items = [];
        }
      } else if (localData) {
        try {
          this.items = JSON.parse(localData);
        } catch (e) {
          console.error('Error parsing wishlist localStorage:', e);
          this.items = [];
        }
      } else {
        this.items = [];
      }
    }

    save() {
      const data = JSON.stringify(this.items);

      // Opslaan in localStorage (backup, permanent)
      try {
        localStorage.setItem(WISHLIST_COOKIE, data);
      } catch (e) {
        console.warn('Could not save to localStorage:', e);
      }

      // Opslaan in cookie (30 dagen, essentieel/functioneel)
      setCookie(WISHLIST_COOKIE, encodeURIComponent(data), COOKIE_DAYS);

      this.updateUI();

      // Trigger custom event voor UI updates
      window.dispatchEvent(new CustomEvent('wishlist:updated', {
        detail: { items: this.items, count: this.items.length }
      }));
    }

    add(productHandle) {
      if (!this.items.includes(productHandle)) {
        this.items.push(productHandle);
        this.save();
        themeLog('✅ Added to wishlist:', productHandle);
        return true;
      }
      return false;
    }

    remove(productHandle) {
      const index = this.items.indexOf(productHandle);
      if (index > -1) {
        this.items.splice(index, 1);
        this.save();
        themeLog('❌ Removed from wishlist:', productHandle);
        return true;
      }
      return false;
    }

    toggle(productHandle) {
      if (this.has(productHandle)) {
        this.remove(productHandle);
        return false;
      } else {
        this.add(productHandle);
        return true;
      }
    }

    has(productHandle) {
      return this.items.includes(productHandle);
    }

    getCount() {
      return this.items.length;
    }

    clear() {
      if (confirm('Weet je zeker dat je je verlanglijst wilt wissen?')) {
        this.items = [];
        this.save();
        this.renderDrawer();
      }
    }

    openDrawer() {
      if (!this.drawer || !this.overlay) return;
      this.isOpen = true;
      this.drawer.classList.add('active');
      this.overlay.classList.add('active');
      document.body.style.overflow = 'hidden';
      this.renderDrawer();
    }

    closeDrawer() {
      if (!this.drawer || !this.overlay) return;
      this.isOpen = false;
      this.drawer.classList.remove('active');
      this.overlay.classList.remove('active');
      document.body.style.overflow = '';
    }

    async renderDrawer() {
      if (!this.drawer) return;

      const count = this.getCount();
      const itemsWrap = this.drawer.querySelector('[data-wishlist-items-wrap]');
      const emptyState = this.drawer.querySelector('[data-wishlist-empty]');
      const footer = this.drawer.querySelector('[data-wishlist-footer]');
      const subtext = this.drawer.querySelector('[data-wishlist-subtext]');
      const drawerCount = this.drawer.querySelector('[data-wishlist-drawer-count]');

      // Update count
      if (drawerCount) {
        drawerCount.textContent = count;
      }

      // Show/hide elements based on count
      if (count === 0) {
        if (itemsWrap) itemsWrap.innerHTML = '';
        if (emptyState) emptyState.classList.remove('hidden');
        if (footer) footer.style.display = 'none';
        if (subtext) subtext.style.display = 'none';
        return;
      }

      if (emptyState) emptyState.classList.add('hidden');
      if (footer) footer.style.display = 'block';
      if (subtext) subtext.style.display = 'block';

      // Show loading
      if (itemsWrap) {
        itemsWrap.innerHTML = '<div class="wishlist-loading"><div class="loading-spinner"></div></div>';
      }

      // Fetch and render products
      try {
        const products = await this.fetchProducts();
        this.renderProducts(products);
      } catch (error) {
        console.error('Error fetching wishlist products:', error);
        if (itemsWrap) {
          itemsWrap.innerHTML = '<div style="padding: 40px 20px; text-align: center; color: #dc2626;">Er ging iets mis bij het laden van je verlanglijst.</div>';
        }
      }
    }

    async fetchProducts() {
      const promises = this.items.map(handle =>
        fetch(`/products/${handle}.js`)
          .then(res => res.json())
          .catch(err => {
            console.error(`Failed to fetch product: ${handle}`, err);
            return null;
          })
      );

      const results = await Promise.all(promises);
      return results.filter(product => product !== null);
    }

    renderProducts(products) {
      const itemsWrap = this.drawer.querySelector('[data-wishlist-items-wrap]');
      if (!itemsWrap) return;

      if (products.length === 0) {
        itemsWrap.innerHTML = '<div style="padding: 40px 20px; text-align: center; color: #6b7280;">Geen producten gevonden in je verlanglijst.</div>';
        return;
      }

      const html = products.map(product => {
        const variant = product.variants[0];
        const price = (variant.price / 100).toFixed(2);
        const imageUrl = product.images[0] ? product.images[0] : '';

        return `
          <div class="wishlist-item" data-wishlist-item="${product.handle}">
            <div class="wishlist-item-image">
              <a href="/products/${product.handle}">
                ${imageUrl ? `<img src="${imageUrl}" alt="${product.title}" loading="lazy">` : ''}
              </a>
            </div>
            <div class="wishlist-item-details">
              <h3 class="wishlist-item-title">
                <a href="/products/${product.handle}">${product.title}</a>
              </h3>
              <p class="wishlist-item-price">€${price}</p>
            </div>
            <div class="wishlist-item-actions">
              <button
                class="wishlist-item-add-btn"
                data-add-to-cart="${variant.id}"
                data-product-handle="${product.handle}"
              >
                In winkelwagen
              </button>
              <button
                class="wishlist-item-remove-btn"
                data-remove-from-wishlist="${product.handle}"
              >
                Verwijderen
              </button>
            </div>
          </div>
        `;
      }).join('');

      itemsWrap.innerHTML = html;
    }

    async addAllToCart() {
      const btn = this.drawer.querySelector('[data-wishlist-add-all]');
      const btnText = btn.querySelector('.btn-text');
      const btnLoading = btn.querySelector('.btn-loading');
      const errorEl = this.drawer.querySelector('[data-wishlist-error]');

      if (!btn) return;

      btn.disabled = true;
      btnText.style.display = 'none';
      btnLoading.style.display = 'inline-flex';
      if (errorEl) errorEl.style.display = 'none';

      try {
        const products = await this.fetchProducts();
        const items = products.map(product => ({
          id: product.variants[0].id,
          quantity: 1
        }));

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items })
        });

        if (!response.ok) throw new Error('Failed to add items to cart');

        // Clear wishlist and open cart drawer
        this.items = [];
        this.save();
        this.closeDrawer();

        // Trigger cart refresh
        if (window.CartDrawer) {
          window.CartDrawer.refresh();
          window.CartDrawer.open();
        } else {
          window.location.href = '/cart';
        }

      } catch (error) {
        console.error('Error adding all to cart:', error);
        if (errorEl) {
          errorEl.textContent = 'Er ging iets mis bij het toevoegen aan de winkelwagen.';
          errorEl.style.display = 'flex';
        }
      } finally {
        btn.disabled = false;
        btnText.style.display = 'inline';
        btnLoading.style.display = 'none';
      }
    }

    updateUI() {
      const count = this.getCount();

      // Update header badge
      const badge = document.querySelector('.wishlist-badge');
      const btn = document.querySelector('.wishlist-btn');

      if (badge) {
        badge.textContent = count;
        badge.style.display = count > 0 ? 'flex' : 'none';
      }

      if (btn) {
        btn.setAttribute('data-wishlist-count', count);
        if (count > 0) {
          btn.classList.add('has-items');
        } else {
          btn.classList.remove('has-items');
        }
      }

      // Update all wishlist buttons on page
      document.querySelectorAll('[data-wishlist-btn]').forEach(button => {
        const handle = button.getAttribute('data-product-handle');
        if (handle) {
          if (this.has(handle)) {
            button.classList.add('active');
            const span = button.querySelector('span');
            if (span) span.textContent = 'In verlanglijst';
          } else {
            button.classList.remove('active');
            const span = button.querySelector('span');
            if (span) span.textContent = 'Verlanglijst';
          }
        }
      });

      // Update drawer if open
      if (this.isOpen) {
        this.renderDrawer();
      }
    }

    attachEventListeners() {
      // Listen to wishlist button clicks on product pages
      document.addEventListener('click', (e) => {
        const wishlistBtn = e.target.closest('[data-wishlist-btn]');
        if (wishlistBtn) {
          e.preventDefault();
          const handle = wishlistBtn.getAttribute('data-product-handle');
          if (handle) {
            this.toggle(handle);
          }
        }

        // Open drawer trigger
        const openTrigger = e.target.closest('[data-wishlist-trigger]');
        if (openTrigger) {
          e.preventDefault();
          this.openDrawer();
        }

        // Close drawer
        const closeTrigger = e.target.closest('[data-wishlist-drawer-close]');
        if (closeTrigger) {
          e.preventDefault();
          this.closeDrawer();
        }

        // Close on overlay click
        const overlayClick = e.target === this.overlay;
        if (overlayClick) {
          this.closeDrawer();
        }

        // Remove from wishlist in drawer
        const removeBtn = e.target.closest('[data-remove-from-wishlist]');
        if (removeBtn) {
          e.preventDefault();
          const handle = removeBtn.getAttribute('data-remove-from-wishlist');
          if (handle) {
            this.remove(handle);
          }
        }

        // Add single item to cart from drawer
        const addToCartBtn = e.target.closest('[data-add-to-cart]');
        if (addToCartBtn && addToCartBtn.hasAttribute('data-product-handle')) {
          e.preventDefault();
          const variantId = addToCartBtn.getAttribute('data-add-to-cart');
          const handle = addToCartBtn.getAttribute('data-product-handle');
          this.addToCartSingle(variantId, handle, addToCartBtn);
        }

        // Add all to cart
        const addAllBtn = e.target.closest('[data-wishlist-add-all]');
        if (addAllBtn) {
          e.preventDefault();
          this.addAllToCart();
        }

        // Clear wishlist
        const clearBtn = e.target.closest('[data-wishlist-clear]');
        if (clearBtn) {
          e.preventDefault();
          this.clear();
        }
      });

      // Close drawer on ESC key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) {
          this.closeDrawer();
        }
      });
    }

    async addToCartSingle(variantId, handle, button) {
      const originalText = button.textContent;
      button.disabled = true;
      button.textContent = 'Bezig...';

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: variantId,
            quantity: 1
          })
        });

        if (!response.ok) throw new Error('Failed to add to cart');

        // Remove from wishlist
        this.remove(handle);

        // Update cart drawer
        if (window.CartDrawer) {
          window.CartDrawer.refresh();
        }

        button.textContent = 'Toegevoegd!';
        setTimeout(() => {
          button.textContent = originalText;
          button.disabled = false;
        }, 1500);

      } catch (error) {
        console.error('Error adding to cart:', error);
        button.textContent = 'Fout!';
        setTimeout(() => {
          button.textContent = originalText;
          button.disabled = false;
        }, 2000);
      }
    }
  }

  // Initialize global wishlist
  window.Wishlist = new Wishlist();

  themeLog('💝 Wishlist initialized:', window.Wishlist.getCount(), 'items');
})();
